import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getPaymentDetail, 
  updatePaymentStatus,
  resendPaymentNotification,
  verifyPayment 
} from '../../services/api';
import { 
  FiArrowLeft, 
  FiRefreshCw, 
  FiUser, 
  FiCreditCard, 
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiDownload,
  FiCopy,
  FiChevronRight,
  FiCalendar,
  FiMail,
  FiPhone,
  FiFileText,
  FiShield,
  FiSend,
  FiExternalLink,
  FiBox,
  FiCheck, 
  FiLoader 
} from 'react-icons/fi';
import { 
  TbArrowsExchange,
  TbReceiptRefund,
  TbWallet
} from 'react-icons/tb';
import { MdOutlinePayment, MdVerified } from 'react-icons/md';


function PaymentDetail() {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [gatewayResponse, setGatewayResponse] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  useEffect(() => {
    fetchPaymentDetail();
  }, [paymentId]);

  useEffect(() => {
    if (payment) {
      try {
        const parsedMetadata = payment.metadata ? JSON.parse(payment.metadata) : null;
        setMetadata(parsedMetadata);
        
        const parsedGatewayResponse = payment.gateway_response ? JSON.parse(payment.gateway_response) : null;
        setGatewayResponse(parsedGatewayResponse);
      } catch (err) {
        console.error('Error parsing JSON data:', err);
      }
    }
  }, [payment]);


  const fetchPaymentDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getPaymentDetail(paymentId);
      
      if (response.success) {
        setPayment(response.data.payment);
        setVerificationResult(null); // Clear previous verification result
      } else {
        setError(response.message || 'Failed to load payment details');
      }
    } catch (err) {
      setError('Failed to load payment details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to change payment status to "${newStatus}"?`)) {
      return;
    }

    try {
      const adminNotes = prompt('Enter admin notes (optional):', '');
      const response = await updatePaymentStatus(paymentId, newStatus, adminNotes || '');

      if (response.success) {
        setSuccessMessage(`Payment status updated to ${newStatus} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchPaymentDetail();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      setError(`Failed to update payment status: ${err.message}`);
    }
  };

  const handleResendNotification = async () => {
    if (!window.confirm('Resend payment notification to user?')) {
      return;
    }

    try {
      const response = await resendPaymentNotification(paymentId);
      if (response.success) {
        setSuccessMessage('Payment notification resent successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(response.message || 'Resend failed');
      }
    } catch (err) {
      setError(`Failed to resend notification: ${err.message}`);
    }
  };

  const handleVerifyPayment = async () => {
    if (!window.confirm('Manually verify this payment with Paystack? This will check the current status with the payment gateway.')) {
      return;
    }

    try {
      setVerifying(true);
      setError('');
      const response = await verifyPayment(paymentId);

      if (response.success) {
        setVerificationResult(response.data);
        setSuccessMessage(`Payment verified! Status: ${response.data.new_status}`);
        setTimeout(() => setSuccessMessage(''), 5000);
        
        // Refresh payment details to get updated status
        setTimeout(() => {
          fetchPaymentDetail();
        }, 1000);
      } else {
        throw new Error(response.message || 'Verification failed');
      }
    } catch (err) {
      setError(`Failed to verify payment: ${err.message}`);
    } finally {
      setVerifying(false);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-4 py-2 text-sm font-medium rounded-full flex items-center justify-center";
    
    const statusConfig = {
      'success': { bg: 'bg-green-100', text: 'text-green-800', label: 'Success', icon: FiCheckCircle },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending', icon: FiClock },
      'failed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed', icon: FiXCircle },
      'cancelled': { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Cancelled', icon: FiXCircle },
      'refunded': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Refunded', icon: TbReceiptRefund },
      'processing': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Processing', icon: FiAlertCircle },
      'abandoned': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Abandoned', icon: FiXCircle }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: FiAlertCircle };
    const Icon = config.icon;
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        <Icon className="mr-2" />
        {config.label}
      </span>
    );
  };

  const getPaymentTypeBadge = () => {
    const baseClasses = "px-4 py-2 text-sm font-medium rounded-full flex items-center";
    
    // Define badge configurations for each payment type
    const badgeConfigs = {
      'deposit': {
        icon: <TbWallet className="mr-2" />,
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Deposit'
      },
      'withdrawal': {
        icon: <TbWallet className="mr-2" />,
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        label: 'Withdrawal'
      },
      'purchase': {
        icon: <MdOutlinePayment className="mr-2" />,
        bg: 'bg-indigo-100',
        text: 'text-indigo-800',
        label: 'Purchase'
      },
      'boost': {
        icon: <TbArrowsExchange className="mr-2" />,
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        label: 'Boost'
      },
      'escrow': {
        icon: <TbReceiptRefund className="mr-2" />,
        bg: 'bg-amber-100',
        text: 'text-amber-800',
        label: 'Escrow'
      }
    };
  
    // Handle old order payment metadata (for backward compatibility)
    if (metadata?.payment_type === 'order' || metadata?.payment_type === 'order_payment') {
      return (
        <span className={`${baseClasses} bg-indigo-100 text-indigo-800`}>
          <MdOutlinePayment className="mr-2" />
          Order Payment
        </span>
      );
    }
  
    // Get config for the payment type
    const config = badgeConfigs[payment.payment_type] || {
      icon: null,
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      label: payment.payment_type || 'Payment'
    };
  
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getGatewayBadge = () => {
    const baseClasses = "px-3 py-1 text-sm font-medium rounded-full";
    
    if (payment.gateway === 'paystack') {
      return <span className={`${baseClasses} bg-orange-100 text-orange-800`}>Paystack</span>;
    } else if (payment.gateway === 'flutterwave') {
      return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Flutterwave</span>;
    } else if (payment.gateway === 'stripe') {
      return <span className={`${baseClasses} bg-purple-100 text-purple-800`}>Stripe</span>;
    }
    
    return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{payment.gateway}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const isOrderPayment = metadata?.payment_type === 'order';
  const isWithdrawal = payment?.payment_type === 'withdrawal';
  const isBoostPayment = payment?.payment_type === 'boost';
  const isEscrowPayment = payment?.payment_type === 'escrow';
  const showVerifyButton = payment?.status === 'pending' && payment?.gateway === 'paystack';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="flex items-center mb-8">
            <div className="h-10 w-10 bg-gray-200 rounded-lg mr-4"></div>
            <div>
              <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-48"></div>
            </div>
          </div>

          {/* Main Card Skeleton */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i}>
                  <div className="h-4 bg-gray-100 rounded w-32 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-48"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs Skeleton */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="h-8 bg-gray-100 rounded w-48 mb-6"></div>
            <div className="h-40 bg-gray-50 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin/finance/payments')}
            className="flex items-center text-gray-600 hover:text-gray-800 mr-4"
          >
            <FiArrowLeft className="mr-2" />
            Back
          </button>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 text-center">
          <FiAlertCircle className="text-red-500 text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Payment</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => navigate('/admin/finance/payments')}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
            >
              Back to Payments
            </button>
            <button
              onClick={fetchPaymentDetail}
              className="px-6 py-3 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="text-center py-16">
          <div className="text-gray-300 text-8xl mb-6">💳</div>
          <h3 className="text-xl font-medium text-gray-700 mb-2">Payment Not Found</h3>
          <p className="text-gray-500 mb-6">The payment you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/finance/payments')}
            className="px-6 py-3 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] font-medium"
          >
            Back to Payments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#f5f5f5] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center mb-4">
              <button
                onClick={() => navigate('/admin/finance/payments')}
                className="flex items-center text-gray-600 hover:text-gray-800 mr-4 transition-colors"
              >
                <FiArrowLeft className="mr-2" />
                Back to Payments
              </button>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-[#0f0e0e]">Payment Details</h1>
              <div className="flex items-center space-x-2">
                {getStatusBadge(payment.status)}
                {getPaymentTypeBadge()}
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600">
              <div className="flex items-center">
                <FiCreditCard className="mr-2" />
                <span className="font-mono font-medium">{payment.reference}</span>
                <button
                  onClick={() => copyToClipboard(payment.reference)}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                  title="Copy Reference"
                >
                  <FiCopy />
                </button>
              </div>
              <div className="flex items-center">
                <FiCalendar className="mr-2" />
                <span>{formatDate(payment.created_at)}</span>
              </div>
              <div className="flex items-center">
                <span className="bg-gray-100 px-3 py-1 rounded-lg text-sm">
                  ID: {payment.payment_id}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-3">
          {showVerifyButton && (
              <button
                onClick={handleVerifyPayment}
                disabled={verifying}
                className="px-4 py-2.5 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-all duration-200 font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <>
                    <FiLoader className="mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <MdVerified className="mr-2" />
                    Verify with Paystack
                  </>
                )}
              </button>
            )}
                        <button
              onClick={fetchPaymentDetail}
              className="px-4 py-2.5 bg-white text-[#0f0e0e] border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
            {payment.status === 'pending' && (
              <button
                onClick={handleResendNotification}
                className="px-4 py-2.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-all duration-200 font-medium flex items-center"
              >
                <FiSend className="mr-2" />
                Resend Notification
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Verification Result */}
      {verificationResult && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <MdVerified className="text-blue-500 text-xl" />
              <h3 className="font-medium text-blue-800">Payment Verification Result</h3>
            </div>
            <button 
              onClick={() => setVerificationResult(null)}
              className="text-blue-700 hover:text-blue-800 text-xl"
            >
              ×
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-blue-600">Paystack Status:</span>
              <span className="ml-2 font-medium">{verificationResult.paystack_status}</span>
            </div>
            <div>
              <span className="text-blue-600">New Status:</span>
              <span className="ml-2 font-medium">{verificationResult.new_status}</span>
            </div>
            <div>
              <span className="text-blue-600">Verified At:</span>
              <span className="ml-2 font-medium">{formatDateTime(verificationResult.verified_at)}</span>
            </div>
          </div>
          
          <div className="mt-3 text-xs text-blue-600">
            Payment ID: {verificationResult.payment_id}
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-500 text-xl" />
            <p className="text-green-700 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-700 hover:text-green-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-500 text-xl" />
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Column - Summary */}
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Summary Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center">
                <FiDollarSign className="mr-2 text-[#ff660e]" />
                Payment Summary
              </h2>
              <div className="text-3xl font-bold text-[#0f0e0e]">
                {formatCurrency(payment.amount)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
                  <p className="text-[#0f0e0e] font-medium">{metadata?.description || 'No description'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Payment Gateway</label>
                  <div className="flex items-center space-x-2">
                    {getGatewayBadge()}
                    {gatewayResponse?.transfer_code && (
                      <span className="text-xs text-gray-500">
                        Transfer: {gatewayResponse.transfer_code}
                      </span>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Created</label>
                  <div className="flex items-center text-gray-600">
                    <FiCalendar className="mr-2" />
                    <span>{formatDateTime(payment.created_at)}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated</label>
                  <div className="flex items-center text-gray-600">
                    <FiClock className="mr-2" />
                    <span>{formatDateTime(payment.updated_at)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Net Amount</label>
                  <p className="text-2xl font-bold text-[#0f0e0e]">
                    {formatCurrency(payment.net_amount)}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Platform Fee:</span>
                    <span className="font-medium">{formatCurrency(payment.platform_fee)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Processor Fee:</span>
                    <span className="font-medium">{formatCurrency(payment.processor_fee)}</span>
                  </div>
                  {metadata?.fee_breakdown && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Total Fees:</span>
                        <span className="font-medium text-red-600">
                          {formatCurrency(metadata.fee_breakdown.total_fees)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Buyer Pays:</span>
                        <span className="font-medium text-green-600">
                          {formatCurrency(metadata.fee_breakdown.buyer_pays_total)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Seller Receives:</span>
                        <span className="font-medium text-green-600">
                          {formatCurrency(metadata.fee_breakdown.seller_receives_net)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                
                {metadata?.fee_breakdown?.summary && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">{metadata.fee_breakdown.summary}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Withdrawal Specific Info */}
            {isWithdrawal && metadata && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-medium text-[#0f0e0e] mb-3">Withdrawal Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {metadata.recipient_code && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Recipient Code</label>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm">{metadata.recipient_code}</span>
                        <button
                          onClick={() => copyToClipboard(metadata.recipient_code)}
                          className="text-gray-400 hover:text-gray-600 ml-2"
                        >
                          <FiCopy />
                        </button>
                      </div>
                    </div>
                  )}
                  {metadata.original_transaction_ref && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Original Transaction</label>
                      <span className="font-mono text-sm">{metadata.original_transaction_ref}</span>
                    </div>
                  )}
                  {metadata.otp_verified !== undefined && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">OTP Verification</label>
                      <span className={`px-2 py-1 text-xs rounded-full ${metadata.otp_verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {metadata.otp_verified ? 'Verified' : 'Not Verified'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Order Payment Specific Info */}
            {isOrderPayment && metadata && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-medium text-[#0f0e0e] mb-3">Order Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {metadata.order_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Order ID</label>
                      <div className="flex items-center">
                        <FiBox className="mr-2 text-gray-400" />
                        <span className="font-medium">#{metadata.order_id}</span>
                      </div>
                    </div>
                  )}
                  {metadata.product_amount && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Product Amount</label>
                      <span className="font-medium">{formatCurrency(metadata.product_amount)}</span>
                    </div>
                  )}
                  {metadata.fee_amount && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Fee Amount</label>
                      <span className="font-medium text-red-600">{formatCurrency(metadata.fee_amount)}</span>
                    </div>
                  )}
                  {metadata.escrow_amount && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Escrow Amount</label>
                      <span className="font-medium text-green-600">{formatCurrency(metadata.escrow_amount)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Boost Payment Specific Info */}
            {payment.payment_type === 'boost' && metadata && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-medium text-[#0f0e0e] mb-3 flex items-center">
                  <TbArrowsExchange className="mr-2 text-purple-600" />
                  Boost Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {metadata.boost_order_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Boost Order ID</label>
                      <div className="flex items-center">
                        <FiBox className="mr-2 text-gray-400" />
                        <span className="font-medium">#{metadata.boost_order_id}</span>
                      </div>
                    </div>
                  )}
                  {metadata.listing_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Listing ID</label>
                      <span className="font-medium">#{metadata.listing_id}</span>
                    </div>
                  )}
                  {metadata.package_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Package ID</label>
                      <span className="font-medium">#{metadata.package_id}</span>
                    </div>
                  )}
                  {metadata.package_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Package Name</label>
                      <span className="font-medium">{metadata.package_name}</span>
                    </div>
                  )}
                  {metadata.payment_method && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Payment Method</label>
                      <span className="font-medium capitalize">{metadata.payment_method}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Escrow Payment Specific Info */}
            {payment.payment_type === 'escrow' && metadata && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-medium text-[#0f0e0e] mb-3 flex items-center">
                  <TbReceiptRefund className="mr-2 text-amber-600" />
                  Escrow Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {metadata.escrow_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Escrow ID</label>
                      <div className="flex items-center">
                        <FiBox className="mr-2 text-gray-400" />
                        <span className="font-medium">#{metadata.escrow_id}</span>
                      </div>
                    </div>
                  )}
                  {metadata.conversation_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Conversation ID</label>
                      <span className="font-medium">#{metadata.conversation_id}</span>
                    </div>
                  )}
                  {metadata.listing_title && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Listing Title</label>
                      <span className="font-medium">{metadata.listing_title}</span>
                    </div>
                  )}
                  {metadata.buyer_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Buyer ID</label>
                      <span className="font-medium">#{metadata.buyer_id}</span>
                    </div>
                  )}
                  {metadata.seller_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Seller ID</label>
                      <span className="font-medium">#{metadata.seller_id}</span>
                    </div>
                  )}
                  {metadata.payment_method && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Payment Method</label>
                      <span className="font-medium capitalize">{metadata.payment_method}</span>
                    </div>
                  )}
                  {metadata.product_amount && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Product Amount</label>
                      <span className="font-medium">{formatCurrency(metadata.product_amount)}</span>
                    </div>
                  )}
                  {metadata.buyer_total_payment && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Buyer Total Paid</label>
                      <span className="font-medium text-green-600">{formatCurrency(metadata.buyer_total_payment)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
              <FiUser className="mr-2 text-[#ff660e]" />
              User Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">User</label>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#ff660e] to-orange-400 flex items-center justify-center text-white font-bold mr-3">
                      {payment.first_name?.charAt(0)}{payment.last_name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-[#0f0e0e]">
                        {payment.first_name} {payment.last_name}
                      </p>
                      <p className="text-sm text-gray-500">ID: {payment.user_id}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                  <div className="flex items-center text-gray-600">
                    <FiMail className="mr-2" />
                    <span>{payment.email}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
                  <div className="flex items-center text-gray-600">
                    <FiPhone className="mr-2" />
                    <span>{payment.phone_number || 'N/A'}</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Current Wallet Balance</label>
                  <div className="flex items-center">
                    <FiCreditCard className="mr-2 text-gray-400" />
                    <span className="text-xl font-bold text-[#0f0e0e]">
                      {formatCurrency(payment.current_wallet_balance)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate(`/admin/finance/wallets/${payment.wallet_id}`)}
                className="text-[#ff660e] hover:text-[#e55c0d] font-medium flex items-center"
              >
                View User Wallet
                <FiChevronRight className="ml-1" />
              </button>
            </div>
          </div>

          {/* Boost/Escrow Context */}
{(isBoostPayment || isEscrowPayment) && metadata && (
  <div className="mt-4 pt-4 border-t border-gray-200">
    <h4 className="text-sm font-medium text-gray-500 mb-2">Payment Context</h4>
    {isBoostPayment && metadata.boost_order_id && (
      <div className="flex items-center text-purple-600 mb-2">
        <TbArrowsExchange className="mr-2" />
        <span>Boost Order: #{metadata.boost_order_id}</span>
      </div>
    )}
    {isEscrowPayment && metadata.escrow_id && (
      <div className="flex items-center text-amber-600 mb-2">
        <TbReceiptRefund className="mr-2" />
        <span>Escrow: #{metadata.escrow_id}</span>
      </div>
    )}
    {metadata.listing_title && (
      <p className="text-sm text-gray-600">{metadata.listing_title}</p>
    )}
  </div>
)}
        </div>

        {/* Right Column - Actions & Info */}
        <div className="space-y-6">
          {/* Status Actions Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
              <FiShield className="mr-2 text-[#ff660e]" />
              Status Management
            </h2>

            <div className="space-y-3">
              {payment.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleStatusUpdate('success')}
                    className="w-full px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg font-medium flex items-center justify-center border border-green-200 transition-colors"
                  >
                    <FiCheckCircle className="mr-2" />
                    Approve Payment
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('failed')}
                    className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg font-medium flex items-center justify-center border border-red-200 transition-colors"
                  >
                    <FiXCircle className="mr-2" />
                    Reject Payment
                  </button>
                </>
              )}

              {payment.status === 'success' && (
                <>
                  <button
                    onClick={() => handleStatusUpdate('refunded')}
                    className="w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-medium flex items-center justify-center border border-blue-200 transition-colors"
                  >
                    <TbReceiptRefund className="mr-2" />
                    Issue Refund
                  </button>
                </>
              )}

              <div className="pt-4 mt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Current Status</p>
                <div className="flex justify-center">
                  {getStatusBadge(payment.status)}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Info Card */}
          {/* Quick Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#0f0e0e] flex items-center mb-6">
              <FiFileText className="mr-2 text-[#ff660e]" />
              Quick Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Payment ID</label>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-gray-600">{payment.payment_id}</span>
                  <button
                    onClick={() => copyToClipboard(payment.payment_id)}
                    className="text-gray-400 hover:text-gray-600"
                    title="Copy ID"
                  >
                    <FiCopy />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">User ID</label>
                <span className="font-medium text-gray-600">{payment.user_id}</span>
              </div>

              {/* Boost/Escrow specific IDs */}
              {metadata?.boost_order_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Boost Order ID</label>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-gray-600 truncate">{metadata.boost_order_id}</span>
                    <button
                      onClick={() => copyToClipboard(metadata.boost_order_id)}
                      className="text-gray-400 hover:text-gray-600 ml-2"
                      title="Copy ID"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              )}

              {metadata?.escrow_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Escrow ID</label>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-gray-600 truncate">{metadata.escrow_id}</span>
                    <button
                      onClick={() => copyToClipboard(metadata.escrow_id)}
                      className="text-gray-400 hover:text-gray-600 ml-2"
                      title="Copy ID"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              )}

              {gatewayResponse?.transfer_code && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Transfer Code</label>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm text-gray-600 truncate">{gatewayResponse.transfer_code}</span>
                    <button
                      onClick={() => copyToClipboard(gatewayResponse.transfer_code)}
                      className="text-gray-400 hover:text-gray-600 ml-2"
                      title="Copy Code"
                    >
                      <FiCopy />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs for Additional Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('metadata')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'metadata' ? 'border-[#ff660e] text-[#ff660e]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              Metadata
            </button>
            <button
              onClick={() => setActiveTab('gateway')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'gateway' ? 'border-[#ff660e] text-[#ff660e]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              Gateway Response
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'metadata' && metadata && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-[#0f0e0e]">Payment Metadata</h3>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(metadata, null, 2))}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium flex items-center"
                >
                  <FiCopy className="mr-2" />
                  Copy JSON
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {JSON.stringify(metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {activeTab === 'gateway' && gatewayResponse && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-[#0f0e0e]">Gateway Response</h3>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(gatewayResponse, null, 2))}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium flex items-center"
                >
                  <FiCopy className="mr-2" />
                  Copy JSON
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {JSON.stringify(gatewayResponse, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {activeTab === 'gateway' && !gatewayResponse && (
            <div className="text-center py-8">
              <FiAlertCircle className="text-gray-300 text-4xl mx-auto mb-4" />
              <p className="text-gray-500">No gateway response available</p>
            </div>
          )}
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default PaymentDetail;